<script setup>
import { useI18n } from 'vue-i18n';
import Avatar from 'next/avatar/Avatar.vue';
import Badge from 'dashboard/components-next/badge/Badge.vue';

defineProps({
  task: { type: Object, required: true },
  layout: {
    type: String,
    default: 'row',
    validator: v => ['row', 'card'].includes(v),
  },
});

const emit = defineEmits(['open', 'toggle']);

const { t } = useI18n();

const PRIORITY_META = {
  urgent: 'text-n-ruby-9',
  high: 'text-n-amber-9',
  medium: 'text-n-blue-9',
  low: 'text-n-slate-9',
};

const priorityLabel = p => t(`TASKS.PRIORITY.${p.toUpperCase()}`);
const waitingLabel = w => t(`TASKS.WAITING_ON.${w.toUpperCase()}`);
const formatDate = d =>
  d
    ? new Date(d).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
      })
    : '';
</script>

<template>
  <!-- Card layout (board) -->
  <div
    v-if="layout === 'card'"
    class="flex flex-col gap-2 p-3 transition-colors cursor-pointer rounded-xl outline-1 outline outline-n-weak bg-n-solid-1 hover:outline-n-slate-6"
    @click="emit('open', task)"
  >
    <div class="flex items-start gap-2">
      <button
        type="button"
        class="flex items-center justify-center w-4 h-4 mt-0.5 border-2 rounded-full shrink-0 transition-colors"
        :class="
          task.status === 'done'
            ? 'bg-n-teal-9 border-n-teal-9 text-white'
            : 'border-n-slate-8 hover:border-n-teal-9 text-transparent'
        "
        @click.stop="emit('toggle', task)"
      >
        <span class="i-lucide-check text-[10px]" />
      </button>
      <span
        class="flex-1 text-sm"
        :class="
          task.status === 'done'
            ? 'text-n-slate-10 line-through'
            : 'text-n-slate-12'
        "
      >
        {{ task.title }}
      </span>
    </div>
    <div class="flex items-center gap-2">
      <span class="font-mono text-xs text-n-slate-9">
        {{ t('TASKS.NUMBER_PREFIX') }}{{ task.display_id }}
      </span>
      <span
        v-if="task.priority && task.priority !== 'medium'"
        v-tooltip.top="priorityLabel(task.priority)"
        class="i-lucide-flag"
        :class="PRIORITY_META[task.priority]"
      />
      <Badge v-if="task.blocked" color="yellow" size="small">
        {{ t('TASKS.BADGES.BLOCKED') }}
      </Badge>
      <span
        v-if="task.due_date"
        class="ml-auto text-xs"
        :class="task.overdue ? 'text-n-ruby-9 font-medium' : 'text-n-slate-10'"
      >
        {{ formatDate(task.due_date) }}
      </span>
      <Avatar
        v-if="task.assignee"
        :name="task.assignee.name"
        :size="20"
        :class="task.due_date ? '' : 'ml-auto'"
      />
    </div>
  </div>

  <!-- Row layout (list) -->
  <div
    v-else
    class="flex items-center gap-3 px-6 py-2.5 cursor-pointer hover:bg-n-alpha-1"
    @click="emit('open', task)"
  >
    <button
      type="button"
      class="flex items-center justify-center w-4 h-4 border-2 rounded-full shrink-0 transition-colors"
      :class="
        task.status === 'done'
          ? 'bg-n-teal-9 border-n-teal-9 text-white'
          : 'border-n-slate-8 hover:border-n-teal-9 text-transparent'
      "
      @click.stop="emit('toggle', task)"
    >
      <span class="i-lucide-check text-[10px]" />
    </button>

    <span class="font-mono text-xs shrink-0 text-n-slate-9">
      {{ t('TASKS.NUMBER_PREFIX') }}{{ task.display_id }}
    </span>

    <span
      class="flex-1 min-w-0 text-sm truncate"
      :class="
        task.status === 'done'
          ? 'text-n-slate-10 line-through'
          : 'text-n-slate-12'
      "
    >
      {{ task.title }}
    </span>

    <!-- Variable badges sit left of the fixed columns -->
    <Badge v-if="task.blocked" color="yellow" size="small">
      {{ t('TASKS.BADGES.BLOCKED') }}
    </Badge>
    <Badge
      v-if="task.waiting_on && task.waiting_on !== 'none'"
      color="gray"
      size="small"
    >
      {{ waitingLabel(task.waiting_on) }}
    </Badge>

    <!-- Fixed-width columns: priority · due date · assignee (always aligned) -->
    <span class="flex justify-center w-5 shrink-0">
      <span
        v-if="task.priority && task.priority !== 'medium'"
        v-tooltip.top="priorityLabel(task.priority)"
        class="i-lucide-flag"
        :class="PRIORITY_META[task.priority]"
      />
    </span>
    <span
      class="text-xs text-right shrink-0 w-14"
      :class="task.overdue ? 'text-n-ruby-9 font-medium' : 'text-n-slate-10'"
    >
      {{ task.due_date ? formatDate(task.due_date) : '' }}
    </span>
    <span class="flex justify-center w-6 shrink-0">
      <Avatar v-if="task.assignee" :name="task.assignee.name" :size="22" />
    </span>
  </div>
</template>
